import React, { useEffect, useRef, useState } from 'react';
import api from '../api/api';
import { LogoMark } from './Logo';
import './auth-modal.css';

const PLAN_NAMES = { free: 'Free', growth: 'Growth', scale: 'Scale' };

const friendlyError = (error) => {
    const status = error?.response?.status;
    const message = error?.response?.data?.error;
    if ((status === 400 || status === 429) && message) return message;
    if (!error?.response) return 'Couldn’t reach the server. Check your connection and try again.';
    return 'We couldn’t add you just now. Please try again in a minute.';
};

// Joining the waitlist from the landing page. `plan` and `interval` record
// which pricing card they came from; `source` which button opened it.
// `noAccess`: they just tried to sign in without an account.
const WaitlistModal = ({ onClose, onSignIn, plan, interval, source, noAccess }) => {
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [website, setWebsite] = useState('');
    const [company, setCompany] = useState(''); // bot trap, hidden from people
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [joined, setJoined] = useState(null); // { already } once they're on the list
    const emailField = useRef(null);

    useEffect(() => {
        emailField.current?.focus();
    }, []);

    useEffect(() => {
        const handleKey = (event) => event.key === 'Escape' && onClose?.();
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [onClose]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        const cleanEmail = email.trim();
        if (!/^\S+@\S+\.\S+$/.test(cleanEmail)) {
            setError('Enter a valid email address.');
            emailField.current?.focus();
            return;
        }
        setBusy(true);
        setError('');
        try {
            const result = await api.joinWaitlist({
                email: cleanEmail,
                name: name.trim(),
                website: website.trim(),
                plan,
                interval,
                source,
                company,
            });
            setJoined({ already: Boolean(result.already) });
        } catch (e) {
            setError(friendlyError(e));
        } finally {
            setBusy(false);
        }
    };

    const planName = PLAN_NAMES[plan];
    const title = noAccess ? 'You don’t have access yet' : 'Join the waitlist';
    let sub = 'We’re letting people in a few at a time. Join and we’ll email you when your spot is ready.';
    if (noAccess) sub = 'There’s no light speed account for that Google login yet. We’re letting people in a few at a time: join the waitlist and we’ll email you when your spot is ready.';
    else if (planName && plan !== 'free') sub = `We’re letting people in a few at a time. Join and we’ll email you when your spot on ${planName} is ready.`;

    return (
        <div className="am-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}>
            <div className="am-card" role="dialog" aria-modal="true" aria-labelledby="am-title">
                <button type="button" className="am-close" aria-label="Close" onClick={onClose}>
                    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
                </button>

                {joined ? (
                    <>
                        <div className="am-head" role="status">
                            <div className="am-done">
                                <svg width="28" height="28" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            </div>
                            <h2 id="am-title">{joined.already ? 'You’re already on the list' : 'You’re on the list'}</h2>
                            <p>
                                {joined.already
                                    ? `We have ${email.trim()} saved. We’ll email you there as soon as your spot is ready.`
                                    : `Thanks! We’ll email ${email.trim()} as soon as your spot is ready.`}
                            </p>
                        </div>
                        <button type="button" className="am-primary" onClick={onClose}>Done</button>
                    </>
                ) : (
                    <>
                        <div className="am-head">
                            <LogoMark size={48} alt="" />
                            <h2 id="am-title">{title}</h2>
                            <p>{sub}</p>
                        </div>

                        <form onSubmit={handleSubmit} noValidate className="am-form">
                            <label className="am-field">
                                <span>Email</span>
                                <input
                                    ref={emailField}
                                    type="email"
                                    autoComplete="email"
                                    inputMode="email"
                                    placeholder="you@company.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                />
                            </label>
                            <label className="am-field">
                                <span>Name <span className="am-optional">(optional)</span></span>
                                <input
                                    type="text"
                                    autoComplete="name"
                                    placeholder="Your name"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                />
                            </label>
                            <label className="am-field">
                                <span>Your product’s website <span className="am-optional">(optional)</span></span>
                                <input
                                    type="url"
                                    inputMode="url"
                                    autoComplete="url"
                                    placeholder="yourproduct.com"
                                    value={website}
                                    onChange={(e) => setWebsite(e.target.value)}
                                />
                            </label>
                            <label className="am-trap" aria-hidden="true">
                                Company
                                <input type="text" tabIndex={-1} autoComplete="off" value={company} onChange={(e) => setCompany(e.target.value)} />
                            </label>

                            {error && <div className="am-error" role="alert">{error}</div>}

                            <button type="submit" className="am-primary" disabled={busy}>
                                {busy ? <span className="am-spinner" /> : 'Join the waitlist'}
                            </button>
                        </form>

                        {onSignIn && (
                            <div className="am-switch">
                                Already have access? <button type="button" className="am-link" onClick={onSignIn}>Sign in</button>
                            </div>
                        )}
                    </>
                )}

                <p className="am-legal">
                    We’ll only email you about your spot. See our{' '}
                    <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
                </p>
            </div>
        </div>
    );
};

export default WaitlistModal;
